<template>
<!-- État succès -->
      <InfoBox v-if="registrationSuccess" type="success">
        <p>Inscription enregistrée — votre compte sera créé après le paiement.</p>
      </InfoBox>
  <div v-else class="inline-registration">
    <button
      type="button"
      class="registration-toggle"
      @click="toggleForm"
      :class="{ 'active': isExpanded }"
    >
      <span class="toggle-icon">{{ isExpanded ? '−' : '+' }}</span>
      <span class="toggle-text">
        {{ isExpanded ? 'Masquer le formulaire' : 'Créer un compte pour gagner du temps et sécuriser votre eTicket' }}
      </span>
    </button>

    <Transition name="slide-fade">
      <div v-if="isExpanded" class="registration-form-wrapper">
        <p class="registration-description">
          Créez votre compte en quelques secondes pour retrouver facilement vos réservations.
        </p>

        <form @submit.prevent="handleSubmit" class="form-group">
          <label for="reg-phone">Téléphone</label>
          <input
            id="reg-phone"
            v-model="form.phone"
            type="tel"
            placeholder="+237690000000"
            required
            :disabled="phoneDisabled"
          />

          <label for="reg-email">
            Email
            <span class="facultatif">(optionnel)</span>
          </label>
          <input
            id="reg-email"
            v-model="form.email"
            type="email"
            placeholder="exemple@contact.cm"
          />

          <PasswordField ref="passwordField" :is-registration="true"/>

          <div class="form-actions">
            <button
              type="button"
              class="btn btn-secondary btn-small"
              @click="resetForm"
            >
              Réinitialiser
            </button>
            <button
              type="submit"
              class="btn btn-primary btn-small"
              :disabled="submitting"
            >
              {{ submitting ? 'Inscription...' : "S'inscrire" }}
            </button>
          </div>
        </form>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import PasswordField from '@/components/PasswordField.vue'
import InfoBox from '@/components/InfoBox.vue'

const props = defineProps({
  phoneValue: {
    type: String,
    default: ''
  },
  phoneDisabled: {
    type: Boolean,
    default: false
  },
  autoExpand: {
    type: Boolean,
    default: false
  },
  registrationSuccess: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['registration-success', 'registration-error'])

const isExpanded = ref(props.autoExpand)
const submitting = ref(false)
const passwordField = ref(null)

const form = reactive({
  phone: props.phoneValue,
  email: '',
  password: '',
  verifyPassword: ''
})

// Synchroniser le téléphone avec le prop parent
watch(() => props.phoneValue, (newVal) => {
  form.phone = newVal
})

const toggleForm = () => {
  isExpanded.value = !isExpanded.value
}

const resetForm = () => {
  if (!props.phoneDisabled) {
    form.phone = ''
  }
  form.email = ''

  if (passwordField.value) {
    passwordField.value.reset?.()
  }
}

const handleSubmit = async () => {
  try {
    submitting.value = true

    const passwordData = passwordField.value.getValues()

    if (!passwordData.isValid) {
      emit('registration-error', 'Les mots de passe ne correspondent pas ou sont invalides.')
      return
    }

    // Émettre les données au parent pour qu'il gère l'inscription
    emit('registration-success', {
      phone: form.phone,
      email: form.email,
      password: passwordData.password,
      passwordVerify: passwordData.verifyPassword
    })

  } catch (err) {
    console.error(err)
    emit('registration-error', "Une erreur est survenue.")
  } finally {
    submitting.value = false
  }
}

// Exposer la méthode pour réinitialiser depuis le parent
defineExpose({
  resetForm,
  toggleForm,
  isExpanded
})
</script>

<style scoped>
.inline-registration {
  width: 100%;
}

.registration-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #e8f5e9, #f1f8e9);
  border: 1px solid var(--color-jungle);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  color: var(--color-text-primary);
  font-weight: 500;
}

.registration-toggle:hover {
  background: linear-gradient(135deg, #d4edda, #e2f0d9);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.registration-toggle.active {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background: var(--color-jungle);
  color: white;
}
.registration-toggle.active:hover {
  transform: translateY(0);
  opacity: 0.9;
}

.toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  font-size: 1.2rem;
  font-weight: bold;
  flex-shrink: 0;
}

.registration-toggle.active .toggle-icon {
  color: white;
}

.toggle-text {
  flex: 1;
  text-align: left;
}

.registration-form-wrapper {
  padding: 1.5rem;
  background: var(--color-background-secondary);
  border: 1px solid var(--color-jungle);
  border-top: none;
  border-bottom-left-radius: var(--border-radius-md);
  border-bottom-right-radius: var(--border-radius-md);
}

.registration-description {
  margin: 0 0 1.25rem 0;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--color-text-primary);
  margin-bottom: -0.5rem;
}

.facultatif {
  font-weight: 400;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-style: italic;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--color-jungle);
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.1);
}

.form-group input:disabled {
  background-color: var(--color-grey-light);
  cursor: not-allowed;
  opacity: 0.6;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn-small {
  padding: 0.65rem 1.25rem;
  font-size: 0.9rem;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

@media (max-width: 768px) {
  .form-actions {
    flex-direction: column;
  }

  .btn-small {
    width: 100%;
  }
}
</style>
